import { RefreshCcw01Icon } from '@untitledui-icons/react/line'

import { Switch } from '@/components/ui/Switch'
import { cn } from '@/lib/utils'
import { isLightColor, isValidHexColor, WIDGET_OFFSET_MAX } from './widgetConfig.serializers'

/**
 * The design's toggle green. The shared Switch defaults to brand teal, so the
 * selected track is recoloured rather than forking the component.
 */
const TOGGLE_CLASS =
  '[&[data-selected]>div]:bg-[#237050] [&[data-selected]>div]:hover:bg-[#1a5440] dark:[&[data-selected]>div]:bg-[#237050]'

const FIELD_CLASS =
  'flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 shadow-xs focus-within:border-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:focus-within:border-gray-500'

const BARE_INPUT_CLASS =
  'min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 outline-none dark:text-gray-100'

// ============================================================================
// Section scaffolding
// ============================================================================

interface WidgetSectionProps {
  title: string
  /** Rendered opposite the title — the "Reset" affordance in the design. */
  action?: React.ReactNode
  children: React.ReactNode
  className?: string
}

export function WidgetSection({ title, action, children, className }: WidgetSectionProps) {
  return (
    <section className={cn('flex flex-col gap-[13px]', className)}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg leading-7 font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  )
}

export function WidgetFieldGroup({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-5">{children}</div>
}

interface WidgetResetButtonProps {
  label: string
  title: string
  onPress: () => void
}

export function WidgetResetButton({ label, title, onPress }: WidgetResetButtonProps) {
  return (
    <button
      type="button"
      title={title}
      onClick={onPress}
      className="inline-flex items-center gap-1 text-xs leading-[18px] text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
    >
      <RefreshCcw01Icon className="size-4 shrink-0" />
      {label}
    </button>
  )
}

interface WidgetFieldRowProps {
  label: string
  /** Set when the control is a group of buttons rather than a single input. */
  htmlFor?: string
  wide?: boolean
  children: React.ReactNode
}

/**
 * The design's label column is fixed, so every control lines up down the form.
 * Label, gap and control add up to exactly the 471px content width of the form
 * column, which is why a wider label is paired with a narrower control: the row
 * stays on one line and every control keeps the same right edge. A translation
 * that outgrows its column runs into the gap rather than wrapping.
 */
export function WidgetFieldRow({ label, htmlFor, wide, children }: WidgetFieldRowProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-16 gap-y-2">
      <label
        htmlFor={htmlFor}
        className={cn(
          'shrink-0 text-sm font-medium whitespace-nowrap text-gray-900 dark:text-gray-100',
          wide ? 'w-14' : 'w-[47px]'
        )}
      >
        {label}
      </label>
      <div className={cn('w-full shrink-0', wide ? 'max-w-[351px]' : 'max-w-[360px]')}>
        {children}
      </div>
    </div>
  )
}

// ============================================================================
// Segmented control (Shape, Size)
// ============================================================================

interface WidgetSegmentedControlProps<T extends string> {
  label: string
  value: T
  options: readonly { value: T; label: string }[]
  onChange: (value: T) => void
}

export function WidgetSegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: WidgetSegmentedControlProps<T>) {
  return (
    <div role="group" aria-label={label} className="flex h-9">
      {options.map((option, index) => {
        const isSelected = option.value === value

        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onChange(option.value)}
            className={cn(
              'min-w-0 flex-1 border px-3 text-sm font-semibold',
              index > 0 && '-ml-px',
              index === 0 && 'rounded-l-md',
              index === options.length - 1 && 'rounded-r-md',
              isSelected
                ? 'z-1 border-gray-950 bg-gray-950 text-white dark:border-gray-100 dark:bg-gray-100 dark:text-gray-900'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

// ============================================================================
// Colour row
// ============================================================================

interface WidgetColorRowProps {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
}

/**
 * Swatch plus hex field. The swatch is the native colour input so the value can
 * be picked as well as typed, and typing only repaints once the hex is complete.
 */
export function WidgetColorRow({ id, label, value, onChange }: WidgetColorRowProps) {
  const isComplete = isValidHexColor(value)
  const swatch = isComplete ? value.trim() : '#ffffff'

  return (
    <WidgetFieldRow label={label} htmlFor={id}>
      <div className={cn(FIELD_CLASS, 'h-9')}>
        <input
          type="color"
          value={swatch}
          aria-label={label}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          className={cn(
            'size-5 shrink-0 cursor-pointer appearance-none rounded-xs border-0 bg-transparent p-0',
            '[&::-webkit-color-swatch]:rounded-xs [&::-webkit-color-swatch-wrapper]:p-0',
            isLightColor(swatch)
              ? '[&::-webkit-color-swatch]:border [&::-webkit-color-swatch]:border-gray-200'
              : '[&::-webkit-color-swatch]:border-0'
          )}
        />
        <input
          id={id}
          type="text"
          autoComplete="off"
          spellCheck={false}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={cn(BARE_INPUT_CLASS, 'uppercase')}
        />
      </div>
    </WidgetFieldRow>
  )
}

// ============================================================================
// Placement
// ============================================================================

interface WidgetCornerPickerProps<T extends string> {
  label: string
  value: T
  options: readonly { value: T; label: string; align: 'left' | 'right' }[]
  onChange: (value: T) => void
}

/** Each tile is a miniature page with the launcher dot in its own corner. */
export function WidgetCornerPicker<T extends string>({
  label,
  value,
  options,
  onChange,
}: WidgetCornerPickerProps<T>) {
  return (
    <div role="group" aria-label={label} className="flex gap-2">
      {options.map((option) => {
        const isSelected = option.value === value

        return (
          <button
            key={option.value}
            type="button"
            title={option.label}
            aria-label={option.label}
            aria-pressed={isSelected}
            onClick={() => onChange(option.value)}
            className={cn(
              'relative h-10 min-w-0 flex-1 rounded-md border',
              isSelected
                ? 'border-gray-950 bg-gray-950 dark:border-gray-100 dark:bg-gray-100'
                : 'border-gray-200 bg-white hover:border-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-500'
            )}
          >
            <span
              className={cn(
                'absolute bottom-[9px] size-2 rounded-full',
                option.align === 'left' ? 'left-[9px]' : 'right-[9px]',
                isSelected ? 'bg-white dark:bg-gray-900' : 'bg-gray-900 dark:bg-gray-100'
              )}
            />
          </button>
        )
      })}
    </div>
  )
}

interface WidgetOffsetFieldProps {
  id: string
  axis: string
  label: string
  unit: string
  value: string
  onChange: (value: string) => void
}

export function WidgetOffsetField({
  id,
  axis,
  label,
  unit,
  value,
  onChange,
}: WidgetOffsetFieldProps) {
  return (
    <span className={cn(FIELD_CLASS, 'h-10 min-w-0 flex-1')}>
      <span aria-hidden="true" className="shrink-0 text-base text-gray-400">
        {axis}
      </span>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/[^\d]/g, ''))}
        max={WIDGET_OFFSET_MAX}
        className={BARE_INPUT_CLASS}
      />
      <span aria-hidden="true" className="shrink-0 text-base text-gray-500">
        {unit}
      </span>
    </span>
  )
}

// ============================================================================
// Toggle card
// ============================================================================

interface WidgetToggleCardProps {
  title: string
  description: string
  isSelected: boolean
  onChange: (isSelected: boolean) => void
  isDisabled?: boolean
  /** Revealed under the toggle row once it is on. */
  children?: React.ReactNode
}

export function WidgetToggleCard({
  title,
  description,
  isSelected,
  onChange,
  isDisabled,
  children,
}: WidgetToggleCardProps) {
  return (
    <div className="rounded-2xl border border-gray-200 p-4 dark:border-gray-700">
      <div className="flex items-start gap-[13px]">
        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col gap-1',
            isDisabled && 'opacity-50'
          )}
        >
          <p className="text-base font-medium text-gray-900 dark:text-gray-100">{title}</p>
          <p className="text-[13px] leading-[18px] text-gray-600 dark:text-gray-400">
            {description}
          </p>
        </div>
        <Switch
          size="sm"
          aria-label={title}
          isSelected={isSelected}
          isDisabled={isDisabled}
          onChange={onChange}
          className={cn('mt-0.5 shrink-0', TOGGLE_CLASS)}
        />
      </div>
      {children}
    </div>
  )
}

interface WidgetInlineNoteProps {
  icon: React.ReactNode
  children: React.ReactNode
}

export function WidgetInlineNote({ icon, children }: WidgetInlineNoteProps) {
  return (
    <p className="mt-3 flex items-start gap-2 text-[13px] leading-[18px] text-gray-600 dark:text-gray-400">
      <span className="mt-px shrink-0 text-gray-500 [&>svg]:size-[15px]">{icon}</span>
      {children}
    </p>
  )
}
